
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useNodes } from '@/hooks/useNodes';
import { useDisciplines } from '@/hooks/useDisciplines';
import { useCreateEdge } from '@/hooks/useCrossDisciplinaryEdges';
import { Link2, Plus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';

interface EdgeCreationFormProps {
  onEdgeCreated: () => void;
}

const EdgeCreationForm: React.FC<EdgeCreationFormProps> = ({ onEdgeCreated }) => {
  const [selectedNode, setSelectedNode] = useState<string>('');
  const [targetNode, setTargetNode] = useState<string>('');
  const [relationshipType, setRelationshipType] = useState<string>('');
  const [filterDiscipline, setFilterDiscipline] = useState<string>('all');

  const { data: allNodes } = useNodes();
  const { data: disciplines } = useDisciplines();
  const createEdge = useCreateEdge();

  const validDisciplines = disciplines?.filter(discipline => discipline.id && discipline.id.trim() !== '') || [];
  const publishedNodes = allNodes?.filter(node => node.status === 'published') || [];

  const relationshipTypes = [
    { value: 'prerequisite', label: 'Prerequisite', description: 'Required before understanding' },
    { value: 'related', label: 'Related', description: 'Similar or connected concepts' },
    { value: 'deepens', label: 'Deepens', description: 'Builds upon or extends' },
    { value: 'critiques', label: 'Critiques', description: 'Challenges or questions' },
    { value: 'applies', label: 'Applies', description: 'Practical application of' },
    { value: 'exemplifies', label: 'Exemplifies', description: 'Example or illustration of' }
  ];

  const filteredNodes = publishedNodes.filter(node => 
    filterDiscipline === 'all' || node.discipline_id === filterDiscipline
  );

  const handleCreateEdge = async () => {
    if (!selectedNode || !targetNode || !relationshipType) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    if (selectedNode === targetNode) {
      toast({ title: 'Error', description: 'Source and target nodes cannot be the same', variant: 'destructive' });
      return;
    }

    try {
      await createEdge.mutateAsync({
        source_node_id: selectedNode,
        target_node_id: targetNode,
        relationship_type: relationshipType,
        metadata: { status: "pending" },
        strength_score: 1.0,
        weight: 1
      });

      toast({ title: 'Success', description: 'Cross-disciplinary connection created and pending approval' });
      setSelectedNode('');
      setTargetNode('');
      setRelationshipType('');
      onEdgeCreated();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create connection', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Create Cross-Disciplinary Connection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Source Node</label>
            <Select value={selectedNode} onValueChange={setSelectedNode}>
              <SelectTrigger>
                <SelectValue placeholder="Select source node..." />
              </SelectTrigger>
              <SelectContent>
                {filteredNodes.map((node) => (
                  <SelectItem key={node.id} value={node.id}>
                    {node.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Target Node</label>
            <Select value={targetNode} onValueChange={setTargetNode}>
              <SelectTrigger>
                <SelectValue placeholder="Select target node..." />
              </SelectTrigger>
              <SelectContent>
                {filteredNodes
                  .filter(node => node.id !== selectedNode)
                  .map((node) => (
                    <SelectItem key={node.id} value={node.id}>
                      {node.title}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Relationship Type</label>
            <Select value={relationshipType} onValueChange={setRelationshipType}>
              <SelectTrigger>
                <SelectValue placeholder="Select relationship..." />
              </SelectTrigger>
              <SelectContent>
                {relationshipTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-black">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Filter by Discipline</label>
            <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
              <SelectTrigger>
                <SelectValue placeholder="All disciplines" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All disciplines</SelectItem>
                {validDisciplines.map((discipline) => (
                  <SelectItem key={discipline.id} value={discipline.id}>
                    {discipline.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button 
          onClick={handleCreateEdge}
          disabled={!selectedNode || !targetNode || !relationshipType || createEdge.isPending}
          className="w-full"
        >
          <Link2 className="h-4 w-4 mr-2" />
          {createEdge.isPending ? 'Creating...' : 'Create Cross-Disciplinary Connection'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default EdgeCreationForm;
